import { useApolloClient } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { useUpdateAccount } from "apollo/hooks/mutations";
import { useEnvContext } from "context/EnvContext";
import {
  ACCOUNT_ROLE_RECEIVER_BASE_ROLE,
  ACCOUNT_STATUS_INACTIVE,
  ACCOUNT_TYPE_PROVIDER_SEARCH,
  ACCOUNT_TYPE_STAFF,
  APP_CAREPROVIDER,
  APP_CLINIC,
  TRACK_EVENTS,
} from "core/consts";
import api from "core/model/api";
import auth from "core/model/api/endpoints/auth";
import { getFirstProviderRole } from "core/model/careproviders";
import Config from "core/model/config";
import { generateSaltedHash, hasCrypto } from "core/model/crypto";
import { getPrivateKey } from "core/model/crypto/cryptoService";
import { getError } from "core/model/utils/errors";
import {
  activateRealUserMonitoring,
  activateSeald,
} from "core/model/utils/featureFlags";
import { isDev } from "core/seald";
import { useSealdContext } from "core/seald/SealdContext";
import { CareproviderRole, SsoTokenDecoded } from "core/types";
import { differenceInMilliseconds } from "date-fns";
import { getSubscriptionInfoWithTimeout } from "dsl/atoms/BrowserNotificationContext/NotificationPermission";
import gql from "graphql-tag";
import jwtDecode from "jwt-decode";
import { useCallback } from "react";
import { useTracking } from "react-tracking";
import { useAppDispatch } from "reduxentities/hooks";
import { LOGGED_OUT, TOKEN_CHANGED } from "reduxentities/slices/authSlice";
import { useTranslations } from "translations";

export type LoginProps = {
  careproviderToLog?: number;
  challenge?: string;
  password: string;
  password_hashed?: string;
  salt?: string;
  token?: string;
  username: string;
};

export type LoginFnError = {
  message?: string;
  needsChallenge?: boolean;
};

type AuthenticateProps = {
  password: string;
  password_hashed: string | undefined;
  salt: string | undefined;
  username: string;
};

let tentativeLoginAccountId: number | undefined;
const setTentativeLoginAccountId = (accountId: number) => {
  tentativeLoginAccountId = accountId;
};

export const getTentativeLoginAccountId = () => tentativeLoginAccountId;

export function activateEncryption(
  currentLocation: AnyObject | null,
): [boolean, { hostname?: string; protocol?: string; reason?: string }] {
  if (!currentLocation) return [false, { reason: "no location" }];

  const correctLocation =
    currentLocation.protocol === "https:" ||
    // testcafe
    currentLocation.hostname.startsWith("10.0") ||
    currentLocation.hostname === "localhost";
  if (!correctLocation)
    return [
      false,
      {
        hostname: currentLocation.hostname,
        protocol: currentLocation.protocol,
        reason: "invalid location",
      },
    ];

  if (!hasCrypto()) {
    return [
      false,
      {
        reason: "no crypto",
      },
    ];
  }

  return [true, {}];
}

export async function getLoginArguments({
  careproviderToLog,
  challenge,
  password,
  token,
  username,
}: Omit<LoginProps, "salt" | "password_hashed">): Promise<LoginProps> {
  const loginArgs: LoginProps = {
    careproviderToLog,
    challenge: challenge ?? undefined,
    password,
    password_hashed: undefined,
    salt: undefined,
    token,
    username,
  };

  if (!token) {
    const { salt } = await auth.getSalt({ user: username });
    const password_hashed = await generateSaltedHash(password, salt);
    return {
      ...loginArgs,
      password_hashed,
      salt,
    };
  }

  return loginArgs;
}

async function handleAuthentication({
  password,
  password_hashed,
  salt,
  username: user,
}: AuthenticateProps) {
  if (password_hashed && salt) {
    return auth.authenticate({
      password_hashed,
      salt,
      user,
    });
  }

  const { salt: genSalt } = await auth.getSalt({ user });
  const passwordHashed = await generateSaltedHash(password, genSalt);

  return auth.authenticate({
    password_hashed: passwordHashed,
    salt: genSalt,
    user,
  });
}

function getTokenExpiration(token: string | undefined) {
  return token ? jwtDecode<SsoTokenDecoded>(token).exp : undefined;
}

export function useLogin() {
  const client = useApolloClient();
  const translations = useTranslations();
  const { app, env } = useEnvContext();
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const [updateAccount] = useUpdateAccount();
  const importSealdIdentity = useSealdContext()?.importSealdIdentity;

  const login = useCallback(
    async ({
      careproviderToLog,
      challenge,
      password,
      password_hashed,
      salt,
      token: ssoToken,
      username,
    }: LoginProps): Promise<undefined | LoginFnError> => {
      if (isDev(env) && !process.env.PR_NAME) {
        const res = await fetch(
          "https://api-staging.recaresolutions.com/cleandata?check",
        );
        const text = await res.text();
        if (text.includes("already running")) {
          return Promise.reject<{ message: string }>({
            message: "cleandata in progress",
          });
        }
      }

      const loginStartTime = new Date();

      let token = ssoToken;
      let expiration = getTokenExpiration(token);

      if (!token) {
        if (!password) {
          dispatch(LOGGED_OUT());
          throw new Error("No token and no password");
        }
        const authenticationResult = await handleAuthentication({
          password,
          password_hashed,
          salt,
          username,
        });
        token = authenticationResult.token;
        expiration = authenticationResult.expiration;
      }

      const subscription_info = await getSubscriptionInfoWithTimeout();

      const {
        account,
        admin_roles,
        careprovider_roles,
        careseeker_roles,
        disable_seald,
        seald_flow,
        seald_force_challenge,
      } = await auth.login({
        activateSeald: activateSeald(app),
        subscription_info,
        token,
      });

      setTentativeLoginAccountId(account.id);

      await client?.clearStore();

      if (account.status === ACCOUNT_STATUS_INACTIVE) {
        dispatch(LOGGED_OUT());
        throw new Error(translations.login.toastInactiveProvider);
      }

      const hasAccessRole = careprovider_roles?.some(
        (role: CareproviderRole) =>
          role.roles?.includes(ACCOUNT_ROLE_RECEIVER_BASE_ROLE),
      );

      if (
        (app === APP_CLINIC &&
          ((!careseeker_roles?.length && !admin_roles?.length) ||
            account.account_type === ACCOUNT_TYPE_PROVIDER_SEARCH)) ||
        (app === APP_CAREPROVIDER && !hasAccessRole && !admin_roles?.length)
      ) {
        dispatch(LOGGED_OUT());
        throw new Error(translations.login.toastInactiveProvider);
      }

      let privateKey;
      let trackContext: AnyObject = {};

      const [activate, context] = activateEncryption(location);

      if (!activate) {
        trackEvent({
          name: TRACK_EVENTS.ENCRYPTION_DEACTIVATED,
          ...context,
        });
      } else {
        trackEvent({ name: TRACK_EVENTS.ENCRYPTION_ACTIVATED });

        if (password) {
          const [decryptedPrivateKey, privateKeyContext] = await getPrivateKey(
            account,
            password,
            {
              api,
              token,
              trackEvent,
            },
          );
          privateKey = decryptedPrivateKey;
          trackContext = {
            decrypted: privateKeyContext.decrypted,
            generated: privateKeyContext.generated,
          };

          if (!privateKey) {
            dispatch(LOGGED_OUT());
            throw new Error("no private key " + JSON.stringify(context));
          }
        }
      }

      if (careseeker_roles?.length) {
        const careseeker = {
          __typename: "Careseeker",
          id: careseeker_roles[0].careseeker?.id,
        };
        const query = gql`
          query Careseekers {
            careseekers {
              id
            }
          }
        `;
        client?.writeQuery({
          data: {
            careseekers: [careseeker],
          },
          query,
        });
      }

      let validCareproviderToLog = false;

      if (careprovider_roles?.length) {
        let careproviderData = getFirstProviderRole(careprovider_roles);
        if (careproviderToLog) {
          const filteredRoles = careprovider_roles.filter(
            (role: CareproviderRole) =>
              role.careprovider?.id === careproviderToLog,
          );

          if (filteredRoles.length === 1) {
            validCareproviderToLog = true;
            careproviderData = filteredRoles[0];
          }
        }

        const careprovider = {
          __typename: "Careprovider",
          id: careproviderData?.careprovider?.id,
        };

        const query = gql`
          query Careproviders {
            careproviders {
              id
            }
          }
        `;
        client?.writeQuery({
          data: {
            careproviders: [careprovider],
          },
          query,
        });
      }

      if (
        !validCareproviderToLog &&
        account.account_type === ACCOUNT_TYPE_STAFF
      ) {
        validCareproviderToLog = true;
        careproviderToLog = careproviderToLog || 1;
      }

      // ********************** SEALD **********************
      if (activateSeald(app) && !disable_seald && importSealdIdentity) {
        console.log("[Seald] account", { account });
        console.log("[Seald] challenge", challenge);
        let mustAuthenticate;
        try {
          mustAuthenticate = await importSealdIdentity({
            account,
            challenge,
            password,
            seald_flow,
            seald_force_challenge,
            token,
            updateAccount,
          });
        } catch (err) {
          const error = getError(err);

          trackEvent({
            error_message: error.message,
            name: TRACK_EVENTS.SEALD_FLOW_FAILED,
            user_information: {
              account_id: account.id,
            },
          });

          const tags = `[account_id:${
            account.id
          }][seald_flow:${seald_flow}][challenge:${challenge ? "yes" : "no"}]`;

          console.error(`Seald flow failed: ${tags} ${error.message}`, error);

          if (activateRealUserMonitoring) {
            datadogRum.addError(err);
          }

          if (error.message.includes("reset_identity")) {
            // prevent login page loading state from flickering
            await new Promise((r) => setTimeout(() => r(null), 5000));
            return Promise.reject({});
          } else {
            dispatch(LOGGED_OUT());
            throw err;
          }
        }
        console.log("[Seald] mustAuthenticate", mustAuthenticate);
        if (mustAuthenticate) {
          return Promise.reject<{ needsChallenge: true }>({
            needsChallenge: true,
          });
        }
      }

      // ********************** /SEALD **********************
      dispatch(
        TOKEN_CHANGED({
          auth_type: password ? "password" : "sso",
          careproviderToLog: validCareproviderToLog
            ? careproviderToLog
            : undefined,
          expiration,
          identification: {
            account,
            admin_roles,
            careprovider_roles,
            careseeker_roles,
            privateKey,
          },
          token,
          token_type: "jwt",
          version: Config.version,
        }),
      );
      trackEvent({
        duration: differenceInMilliseconds(new Date(), loginStartTime),
        name: TRACK_EVENTS.USER_LOGIN_DURATION,
        ...trackContext,
      });
    },
    [
      app,
      client,
      dispatch,
      env,
      getTokenExpiration,
      importSealdIdentity,
      trackEvent,
      translations,
      updateAccount,
    ],
  );

  return login;
}
